/**
 * The organization's own record, as a definition list: legal name, EIN, address, website.
 *
 * ## Why it is a component rather than markup in two places
 *
 * These four rows were written once, inside the ⓘ panel a donor opens from checkout
 * (`OrgInfoPanel`). The organization hub at `/o/<id>` has to show the same four facts, and the
 * expensive part of them is not the layout — it is the **anchor treatment**. The website link is
 * `noopener noreferrer nofollow` with `referrerPolicy="no-referrer"` and the scheme stripped for
 * display, and every one of those is a decision with a reason attached (below). A second copy of
 * that anchor is a second place for one of them to be forgotten, and the way it would be
 * discovered is an organization's analytics showing which donation screen sent a visitor.
 *
 * ## What these facts are, and what they are not
 *
 * The EIN is a **bare identifier**. There is deliberately no sentence anywhere near it about tax
 * deductibility, exempt status, or what a donor may claim: an EIN is not evidence of any of those,
 * and writing one would be a legal statement made by a UI decision rather than by counsel
 * (DEC-001, DEC-011). The same rule holds on the hub, where the number sits in a quieter place and
 * is therefore easier to caption without anyone noticing.
 *
 * Nothing here is a contact route. There is no email, no phone, and no "get in touch" — the public
 * reads that feed this page are built on the guarantee that none of them reaches a contact
 * (`services/api/src/directory-routes.ts`).
 *
 * ## Absent, not empty
 *
 * An organization that has published none of these renders nothing at all rather than a heading
 * over an empty grid. A blank fact list reads as a fact about the organization — that somebody
 * looked and found nothing — when all it means is that a field has not been filled in yet.
 */
import type { OrganizationDetailResponse } from "@givehere/contracts";

/**
 * The legal name appears only when it differs from the name on the mark. For most organizations
 * the two are the same string, and printing "Legal name: Riverside Food Bank" directly beneath a
 * heading reading "Riverside Food Bank" spends a row to say nothing. Where they differ — a trading
 * name, a name in a second script, an umbrella body's registered title — the difference is exactly
 * the thing a donor checking who they are giving to came to see.
 */
const showsLegalName = (organization: OrganizationDetailResponse["organization"]): boolean =>
  organization.legalName !== organization.displayName;

/**
 * Whether this organization has published anything for `OrgFacts` to draw.
 *
 * Exported because the organization hub puts a heading above it, and a heading is a promise that
 * something follows. The alternative was the hub re-deciding emptiness from the same four fields,
 * which is one rule in two places and therefore one rule that will disagree with itself the first
 * time a fifth field is added.
 */
export function hasOrgFacts(organization: OrganizationDetailResponse["organization"]): boolean {
  return (
    showsLegalName(organization) ||
    organization.ein !== null ||
    organization.address !== null ||
    organization.websiteUrl !== null
  );
}

export function OrgFacts({
  organization,
}: {
  organization: OrganizationDetailResponse["organization"];
}) {
  if (!hasOrgFacts(organization)) return null;

  return (
    <dl className="info__facts">
      {showsLegalName(organization) && (
        <>
          <dt>Legal name</dt>
          <dd dir="auto">{organization.legalName}</dd>
        </>
      )}
      {organization.ein && (
        <>
          <dt>EIN</dt>
          {/* Formatted `NN-NNNNNNN` by the server and printed exactly as it arrives. No `dir`
              override: it is digits and a hyphen, and it is not the organization's prose. */}
          <dd>{organization.ein}</dd>
        </>
      )}
      {organization.address && (
        <>
          <dt>Address</dt>
          {/* The organization's own public address, composed server-side. Never a donor's. */}
          <dd dir="auto">{organization.address}</dd>
        </>
      )}
      {organization.websiteUrl && (
        <>
          <dt>Website</dt>
          <dd>
            {/*
              The URL is https-only at the database, at the API, and again here. `noopener` because
              an organization's own site is still a third party to this page, and `no-referrer` so
              their host is not told which donation screen sent the visit — a referrer header from
              `/g/<token>` would hand a tenant's analytics the fact that a named visitor was on a
              checkout screen. `nofollow` because a directory of thousands of organizations that
              passed link equity would become an SEO product nobody asked us to run.
            */}
            <a
              href={organization.websiteUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              referrerPolicy="no-referrer"
            >
              {organization.websiteUrl.replace(/^https:\/\//, "")}
            </a>
          </dd>
        </>
      )}
    </dl>
  );
}
